import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { txAPI, settingsAPI } from '../api';
import api from '../api';
import Money from '../components/Money';
import {
  IconArrowUp, IconArrowDown, IconDoor, IconPaperclip, IconPencil, IconTrash
} from '@tabler/icons-react';

export default function Transactions() {
  const { currentFirm } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ type:'all', category:'all', currency:'all', room:'all', month: new Date().toISOString().slice(0,7) });
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editTx, setEditTx] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const load = useCallback(async () => {
    if (!currentFirm) return;
    setLoading(true);
    try {
      const params = {};
      if (filters.type !== 'all') params.type = filters.type;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.currency !== 'all') params.currency = filters.currency;
      if (filters.room !== 'all') params.room = filters.room;
      if (filters.month) params.month = filters.month;
      const r = await txAPI.getAll(currentFirm.id, { ...params, limit: 200 });
      setTransactions(r.data.transactions);
      setTotal(r.data.total);
    } finally { setLoading(false); }
  }, [currentFirm, filters]);

  useEffect(() => {
    if (!currentFirm) return;
    Promise.all([
      settingsAPI.getCategories(currentFirm.id),
      settingsAPI.getCurrencies(currentFirm.id),
      api.get(`/firms/${currentFirm.id}/transactions/categories`),
    ]).then(([catRes, curRes, txCatRes]) => {
      setCurrencies(curRes.data);
      const settingsCats = catRes.data;
      const settingsNames = new Set(settingsCats.map(c => c.name));
      const extra = txCatRes.data.filter(c => !settingsNames.has(c.category)).map(c => ({ id:c.category, name:c.category, type:c.type }));
      setCategories([...settingsCats, ...extra]);
    });
    api.get(`/firms/${currentFirm.id}/rooms`).then(r => setRooms(r.data || [])).catch(()=>{});
  }, [currentFirm]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (t) => {
    setEditTx(t);
    setEditForm({ type:t.type, description:t.description, amount:t.amount, currency:t.currency, category:t.category, room:t.room||'', tx_date:t.tx_date?.slice(0,10) });
  };

  const cancelEdit = () => { setEditTx(null); setEditForm({}); setEditError(''); };

  const saveEdit = async (e) => {
    e.preventDefault(); setSaving(true); setEditError('');
    try {
      await txAPI.update(currentFirm.id, editTx.id, editForm);
      setEditTx(null); load();
    } catch(err) { setEditError(err.response?.data?.error||'Hata'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu işlemi silmek istiyor musunuz?')) return;
    await txAPI.delete(currentFirm.id, id);
    load();
  };

  const sel = { height:32, padding:'0 8px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:12, background:'var(--bg2)', color:'var(--text)' };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div className="card">
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
          <div style={{ fontSize:15, fontWeight:650, letterSpacing:'-0.02em' }}>
            İşlem geçmişi <span className="count-pill" style={{ marginLeft:6 }}>{total} kayıt</span>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
          <select value={filters.type} onChange={e=>setFilters(f=>({...f,type:e.target.value}))} style={sel}>
            <option value="all">Tüm Türler</option>
            <option value="income">Gelir</option>
            <option value="expense">Gider</option>
          </select>
          <select value={filters.category} onChange={e=>setFilters(f=>({...f,category:e.target.value}))} style={sel}>
            <option value="all">Tüm Kategoriler</option>
            {categories.map((c,i) => <option key={i} value={c.category||c.name}>{c.category||c.name}</option>)}
          </select>
          <select value={filters.currency} onChange={e=>setFilters(f=>({...f,currency:e.target.value}))} style={sel}>
            <option value="all">Tüm Dövizler</option>
            {currencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
          </select>
          {rooms.length > 0 && (
            <select value={filters.room} onChange={e=>setFilters(f=>({...f,room:e.target.value}))} style={sel}>
              <option value="all">Tüm Odalar</option>
              {rooms.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
            </select>
          )}
          <input type="month" value={filters.month} onChange={e=>setFilters(f=>({...f,month:e.target.value}))} style={{...sel,width:145}}/>
          <button onClick={() => setFilters({type:'all',category:'all',currency:'all',room:'all',month:''})} className="btn btn-ghost btn-sm">Temizle</button>
        </div>

        {/* Düzenleme Formu */}
        {editTx && (
          <div className="entry-panel" style={{borderLeftColor:'var(--info)',marginBottom:14}}>
            <div style={{fontSize:13.5,fontWeight:650,color:'var(--info)',marginBottom:13,display:'flex',alignItems:'center',gap:7}}>
              <IconPencil size={15} strokeWidth={1.8}/> İşlemi düzenle
            </div>
            <form onSubmit={saveEdit}>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-end'}}>
                <div><div style={{fontSize:11,color:'var(--text2)',marginBottom:3,fontWeight:500}}>Açıklama</div><input value={editForm.description} onChange={e=>setEditForm(f=>({...f,description:e.target.value}))} style={{height:32,padding:'0 8px',border:'1px solid var(--border)',borderRadius:'var(--radius)',fontSize:12,background:'var(--bg2)',color:'var(--text)',width:200}} required/></div>
                <div><div style={{fontSize:11,color:'var(--text2)',marginBottom:3,fontWeight:500}}>Tutar</div><input type="number" value={editForm.amount} onChange={e=>setEditForm(f=>({...f,amount:e.target.value}))} style={{height:32,padding:'0 8px',border:'1px solid var(--border)',borderRadius:'var(--radius)',fontSize:12,background:'var(--bg2)',color:'var(--text)',width:110}} required/></div>
                <div><div style={{fontSize:11,color:'var(--text2)',marginBottom:3,fontWeight:500}}>Döviz</div><select value={editForm.currency} onChange={e=>setEditForm(f=>({...f,currency:e.target.value}))} style={{height:32,padding:'0 8px',border:'1px solid var(--border)',borderRadius:'var(--radius)',fontSize:12,background:'var(--bg2)',color:'var(--text)',width:80}}>{currencies.map(c=><option key={c.code} value={c.code}>{c.code}</option>)}</select></div>
                <div><div style={{fontSize:11,color:'var(--text2)',marginBottom:3,fontWeight:500}}>Kategori</div><select value={editForm.category} onChange={e=>setEditForm(f=>({...f,category:e.target.value}))} style={{height:32,padding:'0 8px',border:'1px solid var(--border)',borderRadius:'var(--radius)',fontSize:12,background:'var(--bg2)',color:'var(--text)',width:150}}>{categories.filter(c=>c.type===editForm.type||c.category===editForm.category).map((c,i)=><option key={i} value={c.category||c.name}>{c.category||c.name}</option>)}</select></div>
                <div><div style={{fontSize:11,color:'var(--text2)',marginBottom:3,fontWeight:500}}>Tarih</div><input type="date" value={editForm.tx_date} onChange={e=>setEditForm(f=>({...f,tx_date:e.target.value}))} style={{height:32,padding:'0 8px',border:'1px solid var(--border)',borderRadius:'var(--radius)',fontSize:12,background:'var(--bg2)',color:'var(--text)',width:145}} required/></div>
                <button type="submit" disabled={saving} style={{height:32,padding:'0 14px',background:'var(--blue)',color:'#fff',border:'none',borderRadius:'var(--radius)',fontSize:12,cursor:'pointer',fontWeight:500}}>{saving?'Kaydediliyor…':'Güncelle'}</button>
                <button type="button" onClick={cancelEdit} style={{height:32,padding:'0 10px',background:'none',border:'1px solid var(--border)',borderRadius:'var(--radius)',fontSize:12,cursor:'pointer',color:'var(--text2)'}}>İptal</button>
              </div>
              {editError&&<div style={{color:'var(--red)',fontSize:12,marginTop:8}}>{editError}</div>}
            </form>
          </div>
        )}

        {loading ? (
          <div style={{textAlign:'center',padding:'2rem',color:'var(--text3)'}}>Yükleniyor...</div>
        ) : transactions.length === 0 ? (
          <div style={{textAlign:'center',padding:'2rem',color:'var(--text3)',fontSize:13}}>İşlem bulunamadı.</div>
        ) : transactions.map(t => <TxRow key={t.id} t={t} firmId={currentFirm.id} onDelete={() => handleDelete(t.id)} onEdit={() => startEdit(t)} />)}
      </div>
    </div>
  );
}

export function TxRow({ t, onDelete, onEdit, firmId }) {
  const [showReceipt, setShowReceipt] = useState(false);
  const isIncome = t.type === 'income';
  const isForeign = t.currency !== 'TRY';
  const hasReceipt = !!t.receipt_path;
  const tone = isIncome ? 'pos' : 'neg';

  return (
    <div className="tx-row">
      <span className={`tx-dir ${isIncome ? 'tx-dir-pos' : 'tx-dir-neg'}`}>
        {isIncome ? <IconArrowUp size={14} strokeWidth={2.2} /> : <IconArrowDown size={14} strokeWidth={2.2} />}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 550, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {t.description}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
          <span className={`badge ${isIncome ? 'badge-green' : 'badge-red'}`}>{t.category}</span>
          <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t.tx_date?.slice(0, 10)}</span>
          {t.room && <span className="badge badge-gray"><IconDoor size={11} strokeWidth={1.8} />{t.room}</span>}
          {t.created_by && <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>· {t.created_by}</span>}
          {hasReceipt && (
            <button type="button" onClick={() => setShowReceipt(v => !v)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, color: 'var(--info)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <IconPaperclip size={12} strokeWidth={1.8} /> Fiş
            </button>
          )}
        </div>
        {showReceipt && hasReceipt && (
          <a href={`/api/firms/${firmId}/transactions/${t.id}/receipt`} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 12, marginTop: 5, display: 'inline-block' }}>
            Fişi görüntüle →
          </a>
        )}
      </div>

      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <Money value={t.amount_try} tone={tone} sign />
        {isForeign && (
          <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
            {t.currency} {parseFloat(t.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
        {onEdit && (
          <button type="button" onClick={onEdit} className="btn btn-ghost btn-sm btn-icon" aria-label="İşlemi düzenle">
            <IconPencil size={14} strokeWidth={1.7} />
          </button>
        )}
        <button type="button" onClick={onDelete} className="btn btn-sm btn-icon" aria-label="İşlemi sil"
          style={{ background: 'var(--neg-tint)', borderColor: 'var(--neg-line)', color: 'var(--neg)' }}>
          <IconTrash size={14} strokeWidth={1.7} />
        </button>
      </div>
    </div>
  );
}
